import { Component, Inject, OnInit } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CURRENT_ORGANIZATION_MANAGEMENT_SERVICE,
  CurrentOrganizationManagementService,
} from '../../organization/current-organization-management.service';
import {
  ChannelConnection,
  ChannelType,
  EventBusService,
} from '@onep/front-common-lib/event-bus';
import { CustomizationDataService } from '../customization-data/customization-data.service';
import { fromPromise } from 'rxjs/internal-compatibility';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'shared-info-refresh-session',
  templateUrl: './refresh-session.component.html',
  styleUrls: ['./refresh-session.component.scss'],
})
export class RefreshSessionComponent implements OnInit {
  private static SAVED_URL_KEY = 'saved_url';
  private resetPasswordChannel: ChannelConnection<string>;
  private resetPhoneChannel: ChannelConnection<string>;
  isCheckingAuth = true;

  constructor(
    private securityService: OidcSecurityService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(CURRENT_ORGANIZATION_MANAGEMENT_SERVICE)
    private currentOrgService: CurrentOrganizationManagementService,
    private eventBus: EventBusService,
    private customizationDataService: CustomizationDataService
  ) {
    this.resetPasswordChannel = eventBus.connectToChannel<string>(
      'shared-info',
      'reset-password',
      { type: ChannelType.event }
    );
    this.resetPhoneChannel = eventBus.connectToChannel<string>(
      'shared-info',
      'reset-phone',
      { type: ChannelType.event }
    );
  }

  ngOnInit(): void {
    if (this.route.snapshot.queryParamMap.has('reset-password')) {
      this.specialFlow('reset-password', 'reset-password');
    } else if (this.route.snapshot.queryParamMap.has('invitation')) {
      this.specialFlow('invitation', 'invitation');
    } else if (
      (this.route.snapshot.queryParamMap.get('type') === 'sso' &&
        this.route.snapshot.queryParamMap.has('token')) ||
      this.route.snapshot.queryParamMap.has('sso')
    ) {
      if (this.route.snapshot.queryParamMap.has('sso')) {
        this.securityService.checkAuth().subscribe((isAuth) => {
          this.specialFlow('sso', 'sso');
        });
      } else {
        this.securityService.checkAuth().subscribe((isAuth) => {
          this.specialFlow('sso', 'token');
        });
      }
    } else {
      this.securityService.checkAuth().subscribe((isAuth) => {
        this.standardFlow(isAuth);
      });
    }
    this.resetPasswordChannel.receiver.subscribe((resetPasswordSut) => {
      this.saveUrlAndAuthorize('/profile/info', {
        'reset-password': resetPasswordSut,
        'from-beneficiary': true,
      });
    });
    this.resetPhoneChannel.receiver.subscribe((resetPhoneSut) => {
      this.saveUrlAndAuthorize('/profile/info', {
        invitation: resetPhoneSut,
        'from-beneficiary': true,
      });
    });
  }

  /**
   * When a reset-password or an invitation is requested.
   */
  private specialFlow(authParamKey: string, paramKey: string): void {
    const customParams: any = {};
    customParams[authParamKey] = this.route.snapshot.queryParamMap.get(
      paramKey
    ) as string;
    this.securityService
      .checkAuth()
      .pipe(
        switchMap((value) => {
          if (value) {
            return this.securityService.logoffAndRevokeTokens();
          }
          return of(null);
        })
      )
      .subscribe(() => {
        if (
          (authParamKey === 'invitation' ||
            authParamKey === 'reset-password') &&
          window.innerWidth < 1200 &&
          environment.mobileAppUrl
        ) {
          window.location.href = `${environment.mobileAppUrl}${authParamKey}=${customParams[authParamKey]}`;
          setTimeout(() => {
            this.saveUrlAndAuthorize(undefined, customParams);
          }, 200);
        } else {
          this.saveUrlAndAuthorize(undefined, customParams);
        }
      });
  }

  /**
   * When a standard flow is done.
   */
  private standardFlow(isAuth: boolean): void {
    if (isAuth) {
      this.navigateToSavedUrl()
        .pipe(
          switchMap(() => this.currentOrgService.computeCurrentOrganization())
        )
        .subscribe(() => {
          this.isCheckingAuth = false;
        });
    } else {
      this.saveUrlAndAuthorize(this.router.url, undefined);
    }
  }

  private saveUrlAndAuthorize(
    url?: string,
    customParams?: { [key: string]: string | number | boolean } | undefined
  ): void {
    if (url != null) {
      // Remove the code query param from the URL to make sure
      try {
        const urlSanitizer = new URL(url, 'https://foobar.com');
        urlSanitizer.searchParams.delete('code');
        urlSanitizer.searchParams.delete('state');
        const sanitizedUrl = urlSanitizer.pathname + urlSanitizer.search;
        console.log(`Will redirect to ${sanitizedUrl} after auth.`);
        window.localStorage.setItem(
          RefreshSessionComponent.SAVED_URL_KEY,
          sanitizedUrl
        );
      } catch (e) {
        console.error(
          `Will not be able to redirect to "${url}", invalid url: `,
          e
        );
      }
    }

    this.customizationDataService
      .getCustomizationData()
      ?.subscribe((preference) => {
        if (preference) {
          if (preference?.primaryColor) {
            customParams = { ...customParams, color: preference?.primaryColor };
          }
          if (preference?.logoUrl) {
            customParams = { ...customParams, logoUrl: preference?.logoUrl };
          }
        }
        this.securityService.authorize({
          customParams,
        });
      });
  }

  private navigateToSavedUrl(): Observable<boolean> {
    const url = window.localStorage.getItem(
      RefreshSessionComponent.SAVED_URL_KEY
    );
    if (url != null) {
      console.log(`Redirecting to ${url} post-auth...`);
      const navObservable = fromPromise(
        this.router.navigateByUrl(url).catch((e) => {
          console.error(
            'Unable to navigate to SAVED_URL ' + url + ' after auth!',
            e
          );
          return false;
        })
      );
      window.localStorage.removeItem(RefreshSessionComponent.SAVED_URL_KEY);
      return navObservable;
    }
    return of(true);
  }
}
